import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { WelcomeSignup } from "@/components/WelcomeSignup";
import { readConsent } from "@/lib/analytics";

const KEY = "gf_welcome_popup_seen";

export const LegalLinks = ({ text = "By continuing you agree to our", className = "", refunds = false }) => (
  <p className={`text-xs ${className}`}>
    {text} <Link to="/policies/terms" data-testid="legal-terms-link" className="underline underline-offset-2">Terms</Link> and{" "}
    <Link to="/privacy" data-testid="legal-privacy-link" className="underline underline-offset-2">Privacy Policy</Link>.
    {refunds && <>{" "}See our <Link to="/refund-policy" data-testid="legal-refund-link" className="underline underline-offset-2">Refund Policy</Link>.</>}
  </p>
);

export const SignupForm = ({ source, onDone, testPrefix = "email-popup", cta = "I'm in", success = "You're in. Grandpa's proud." }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/subscribe", { email, source });
      toast.success(success);
      setEmail("");
      onDone?.();
    } catch (err) {
      toast.error(errMsg(err, "That email looks sus."));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
      <input data-testid={`${testPrefix}-email-input`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
        className="sm:flex-1 h-12 min-h-12 shrink-0 rounded-full border border-navy/15 px-5 bg-white focus:outline-none focus:ring-4 focus:ring-aqua" />
      <button data-testid={`${testPrefix}-submit-button`} disabled={busy} className="btn-pop bg-navy text-sunny whitespace-nowrap">{busy ? "..." : cta}</button>
    </form>
  );
};

const isMobile = () => window.matchMedia("(max-width: 767px)").matches;
const NO_POPUP = /^\/(payment|subscribe)(\/|$)/;

const PopupBody = () => (
  <>
    <p className="font-display text-2xl leading-tight">Get 10% off your first order</p>
    <p data-testid="email-popup-text" className="mt-1 mb-4 text-sm text-navy/80">Join the Grandpa Finn's list for new arrivals and offers.</p>
    <WelcomeSignup source="popup" testPrefix="email-popup" />
    <p data-testid="email-popup-legal" className="mt-3 text-xs text-navy/70">
      See our <Link to="/privacy" data-testid="email-popup-privacy-link" className="underline underline-offset-2">Privacy Policy</Link>.
    </p>
  </>
);

export const EmailPopup = () => {
  const [open, setOpen] = useState(false);
  const [mobile] = useState(isMobile);
  const { pathname } = useLocation();
  const { cartOpen } = useStore();
  const blocked = NO_POPUP.test(pathname);
  const cartRef = useRef(cartOpen);
  cartRef.current = cartOpen;
  useEffect(() => {
    if (blocked || localStorage.getItem(KEY)) return;
    let t;
    const show = () => {
      if (localStorage.getItem(KEY)) return;
      clearTimeout(t);
      if (cartRef.current || !readConsent()) { t = setTimeout(show, 3000); return; }
      localStorage.setItem(KEY, "1"); setOpen(true);
    };
    t = setTimeout(show, 15000);
    const onLeave = (e) => { if (!e.relatedTarget && e.clientY <= 0) show(); };
    if (!mobile) document.addEventListener("mouseout", onLeave);
    return () => { clearTimeout(t); document.removeEventListener("mouseout", onLeave); };
  }, [mobile, blocked]);
  const close = () => setOpen(false);
  if (!open || blocked || cartOpen) return null;
  if (mobile) return (
    <div data-testid="email-popup" role="dialog" aria-label="Get 10% off your first order"
      className="fixed inset-x-2 bottom-2 z-40 max-h-[85vh] overflow-y-auto rounded-2xl border border-navy/15 bg-sand p-4 pr-12 shadow-lg">
      <button data-testid="email-popup-dismiss-button" onClick={close} aria-label="Close" className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white"><X className="h-4 w-4" /></button>
      <PopupBody />
    </div>
  );
  return (
    <Dialog open onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="email-popup" className="bg-sand border border-navy/15 shadow-md rounded-3xl max-w-md">
        <DialogTitle className="sr-only">Get 10% off your first order</DialogTitle>
        <DialogDescription className="sr-only">Join the Grandpa Finn's list for new arrivals and offers.</DialogDescription>
        <div className="pr-6"><PopupBody /></div>
      </DialogContent>
    </Dialog>
  );
};
